import { useMutation } from '@apollo/client/react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { CreateFeedbackDocument } from 'src/graphql/actions/profile/createFeedback.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import env from 'env';

const feedbackSchema = z.object({
  feedback: z.string().trim().min(1, 'Feedback is required'),
  terms: z.boolean().refine((data) => data, {
    message: 'You must agree to the terms and conditions',
  }),
});

type IFeedbackForm = z.infer<typeof feedbackSchema>;

const FeedbackForm = () => {
  const [createFeedback] = useMutation(CreateFeedbackDocument);
  const form = useForm<IFeedbackForm>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      feedback: '',
      terms: false,
    },
    mode: 'onChange',
  });
  const onSubmit = async ({ feedback }: IFeedbackForm) => {
    try {
      const { data } = await createFeedback({
        variables: {
          message: feedback,
        },
      });

      if (data?.createFeedback?.success) {
        toast.success('Feedback sent!');
        form.reset();
      } else {
        throw new ClientError(data?.createFeedback?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">Share your feedback</CardTitle>
        <CardDescription>We&apos;d like to know what&apos;s on your mind.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="feedback"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Feedback</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[106px]" placeholder="Share your thoughts here." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="terms"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="text-sm font-normal mb-0">
                      I agree with the Codekeeper{' '}
                      <a
                        href={env.CODEKEEPER_TERMS_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="underline! text-foreground!"
                      >
                        terms and conditions
                      </a>
                      .
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              className="w-full"
              type="submit"
              disabled={!form.formState.isValid}
              isLoading={form.formState.isSubmitting}
            >
              Share your feedback
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default FeedbackForm;
